import { SlicePipe } from '@angular/common';
import { Component, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { RouterModule } from '@angular/router';

import { MomentModule } from 'ngx-moment';

import { FollowButtonComponent } from '@recruitee/candidate-ui';
import { trackByProp } from '@recruitee/common';
import { TranslatePipe } from '@recruitee/i18n';
import { AssignedLocationsInfoContainerComponent } from '@recruitee/locations';
import { JobsIdMap } from '@recruitee/offers-data';
import type { OfferBrief } from '@recruitee/offers-types';
import { RequisitionStatus } from '@recruitee/requisitions-types';
import type { Requisition } from '@recruitee/requisitions-types';
import {
  DropdownWidth,
  ClickToCopyDirective,
  ButtonComponent,
  HeadingComponent,
  IconComponent,
  IconButtonComponent,
  TruncateComponent,
  DividerComponent,
  BadgeComponent,
  DropdownItemComponent,
  DropdownComponent,
  OfferStatusIndicatorComponent,
  RtTooltip,
  ConnectedOverlayDirective,
  OverlayOriginDirective,
} from '@recruitee/user-interface';

import { RequisitionsCommonTextsService } from '../../../services/requisitions-common-texts.service';
import { RequisitionsStatusBadgeComponent } from '../../requisitions-status-badge/requisitions-status-badge.component';

@Component({
  selector: 'rt-requisitions-list-tile',
  templateUrl: './requisitions-list-tile.component.html',
  styleUrls: ['./requisitions-list-tile.component.less'],
  imports: [
    BadgeComponent,
    ButtonComponent,
    ClickToCopyDirective,
    ConnectedOverlayDirective,
    DividerComponent,
    DropdownComponent,
    DropdownItemComponent,
    HeadingComponent,
    IconButtonComponent,
    IconComponent,
    MomentModule,
    OfferStatusIndicatorComponent,
    OverlayOriginDirective,
    RequisitionsStatusBadgeComponent,
    RouterModule,
    RtTooltip,
    TruncateComponent,
    TranslatePipe,
    AssignedLocationsInfoContainerComponent,
    SlicePipe,
    FollowButtonComponent,
  ],
})
export class RequisitionsListTileComponent {
  @Input() public requisition: Requisition;
  @Input() public jobsMap: JobsIdMap;
  @Input() public canAddJobs: boolean;
  @Input() public canEditRequisitions: boolean;
  @Input() public isArchived: boolean;

  @Output() public openRequisition: EventEmitter<number> = new EventEmitter();
  @Output() public showJobsList: EventEmitter<OfferBrief[]> = new EventEmitter();
  @Output() public follow: EventEmitter<number> = new EventEmitter();
  @Output() public unfollow: EventEmitter<number> = new EventEmitter();
  @Output() public duplicate: EventEmitter<number> = new EventEmitter();
  @Output() public archive: EventEmitter<Requisition> = new EventEmitter();
  @Output() public retrieve: EventEmitter<Requisition> = new EventEmitter();
  @Output() public delete: EventEmitter<Requisition> = new EventEmitter();
  @Output() public cancelRequest: EventEmitter<number> = new EventEmitter();
  @Output() public addJob: EventEmitter<number> = new EventEmitter();

  public departmentLabelTooltipEnabled = false;

  public RequisitionStatus: typeof RequisitionStatus = RequisitionStatus;
  public trackById = trackByProp<{ id: number }>({ prop: 'id' });
  public DropdownWidth = DropdownWidth;

  @ViewChild('departmentRef') public set departmentRef(departmentRef: ElementRef) {
    if (departmentRef) this.setDepartmentTooltipEnabled(departmentRef);
  }

  constructor(private requisitionsCommonTexts: RequisitionsCommonTextsService) {}

  public get openingsCountText(): string {
    return this.requisitionsCommonTexts.getJobOpeningsText(this.requisition.openings);
  }

  public hasAccessToJob(id: number): boolean {
    return !!this.jobsMap[id];
  }

  private setDepartmentTooltipEnabled(departmentRef: ElementRef) {
    this.departmentLabelTooltipEnabled =
      departmentRef.nativeElement.offsetWidth < departmentRef.nativeElement.scrollWidth;
  }
}
